import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { adminApi } from '../api'
import type { Notice } from '../api/notice'
import { toastUtil } from '../../utils/toast'
import AdminSidebar from '../components/AdminSidebar'
import AdminInput from '../components/AdminInput'

const AdminNotices: React.FC = () => {
  const navigate = useNavigate()
  const [notices, setNotices] = useState<Notice[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [lastPage, setLastPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [selectedNotice, setSelectedNotice] = useState<Notice | null>(null)
  const [formData, setFormData] = useState({ title: '', content: '' })

  const fetchNotices = useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await adminApi.getNotices({
        page: currentPage,
        per_page: 20,
        search: search || undefined,
      })
      setNotices(response.data)
      setLastPage(response.last_page)
      setTotal(response.total)
    } catch (error: any) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/admin/login')
      } else {
        toastUtil.error('공지사항 목록을 불러오는데 실패했습니다')
      }
    } finally {
      setIsLoading(false)
    }
  }, [currentPage, search, navigate])

  useEffect(() => {
    const adminToken = localStorage.getItem('admin_token')
    if (!adminToken) {
      navigate('/admin/login')
      return
    }
    fetchNotices()
  }, [navigate, fetchNotices])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setCurrentPage(1)
    fetchNotices()
  }

  const handleCreate = () => {
    setFormData({ title: '', content: '' })
    setIsCreateModalOpen(true)
  }

  const handleEdit = (notice: Notice) => {
    setSelectedNotice(notice)
    setFormData({ title: notice.title, content: notice.content })
    setIsEditModalOpen(true)
  }

  const handleDelete = async (id: number) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) {
      return
    }

    try {
      await adminApi.deleteNotice(id)
      toastUtil.success('공지사항이 삭제되었습니다')
      fetchNotices()
    } catch (error: any) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/admin/login')
      } else {
        toastUtil.error('공지사항 삭제 중 오류가 발생했습니다')
      }
    }
  }

  const handleSubmitCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await adminApi.createNotice(formData)
      toastUtil.success('공지사항이 생성되었습니다')
      setIsCreateModalOpen(false)
      setFormData({ title: '', content: '' })
      fetchNotices()
    } catch (error: any) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/admin/login')
      } else {
        const errorMessage =
          error.response?.data?.message ||
          '공지사항 생성 중 오류가 발생했습니다'
        toastUtil.error(errorMessage)
      }
    }
  }

  const handleSubmitEdit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedNotice) return

    try {
      await adminApi.updateNotice(selectedNotice.id, formData)
      toastUtil.success('공지사항이 수정되었습니다')
      setIsEditModalOpen(false)
      setSelectedNotice(null)
      setFormData({ title: '', content: '' })
      fetchNotices()
    } catch (error: any) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/admin/login')
      } else {
        const errorMessage =
          error.response?.data?.message ||
          '공지사항 수정 중 오류가 발생했습니다'
        toastUtil.error(errorMessage)
      }
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <div className="min-h-screen bg-backgroundColorBg2 flex">
      <AdminSidebar />

      <div className="flex-1 flex flex-col">
        <header className="bg-white border-b border-brandPrimary-10% sticky top-0 z-10">
          <div className="px-24px py-16px flex items-center justify-end">
            <button
              onClick={() => {
                localStorage.removeItem('admin_token')
                navigate('/admin/login')
              }}
              className="text-style-button2 text-textColorText2 hover:text-brown-80%-main transition-colors"
            >
              로그아웃
            </button>
          </div>
        </header>

        <main className="flex-1 px-24px py-32px">
          <div className="bg-white rounded-16px shadow-sm p-32px">
            <div className="flex items-center justify-between mb-24px">
              <h2 className="text-style-title2 text-textColorText2">
                공지사항 관리
              </h2>
              <div className="flex items-center gap-16px">
                <div className="text-style-body2 text-textColorText2">
                  총 {total.toLocaleString()}개
                </div>
                <button
                  onClick={handleCreate}
                  className="py-12px px-24px bg-4B3621-80% text-white rounded-8px text-style-button2 hover:bg-4B3621-80%-70% transition-colors"
                >
                  공지사항 작성
                </button>
              </div>
            </div>

            <form onSubmit={handleSearch} className="mb-24px">
              <div className="flex gap-12px">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="제목, 내용으로 검색"
                  className={`
                  flex-1
                  py-12px
                  px-16px
                  border
                  border-brandPrimary-20%
                  rounded-8px
                  text-style-body1
                  text-textColorText2
                  bg-transparent
                  focus:outline-none
                  focus:border-brandPrimary
                  placeholder:text-textColorText2
                `}
                />
                <button
                  type="submit"
                  className="py-12px px-24px bg-4B3621-80% text-white rounded-8px text-style-button2 hover:bg-4B3621-80%-70% transition-colors"
                >
                  검색
                </button>
              </div>
            </form>

            {isLoading ? (
              <div className="flex justify-center items-center py-80px">
                <div className="text-style-body2 text-textColorText2">
                  로딩 중...
                </div>
              </div>
            ) : notices.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-80px">
                <div className="text-style-body2 text-textColorText2 mb-8px">
                  공지사항이 없습니다.
                </div>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b border-brandPrimary-10%">
                        <th className="text-left py-12px px-16px text-style-labelM text-textColorText2">
                          ID
                        </th>
                        <th className="text-left py-12px px-16px text-style-labelM text-textColorText2">
                          제목
                        </th>
                        <th className="text-left py-12px px-16px text-style-labelM text-textColorText2">
                          작성일
                        </th>
                        <th className="text-left py-12px px-16px text-style-labelM text-textColorText2">
                          관리
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {notices.map((notice) => (
                        <tr
                          key={notice.id}
                          className="border-b border-brandPrimary-5% hover:bg-backgroundColorBg2 transition-colors"
                        >
                          <td className="py-12px px-16px text-style-body2 text-textColorText2">
                            {notice.id}
                          </td>
                          <td className="py-12px px-16px text-style-body2 text-textColorText2">
                            {notice.title}
                          </td>
                          <td className="py-12px px-16px text-style-body2 text-textColorText2">
                            {formatDate(notice.created_at)}
                          </td>
                          <td className="py-12px px-16px">
                            <div className="flex gap-8px">
                              <button
                                onClick={() => handleEdit(notice)}
                                className={`
                                  py-8px
                                  px-16px
                                  bg-4B3621-80%
                                  text-white
                                  rounded-8px
                                  text-style-button2
                                  hover:bg-4B3621-80%-70%
                                  transition-colors
                                `}
                              >
                                수정
                              </button>
                              <button
                                onClick={() => handleDelete(notice.id)}
                                className={`
                                  py-8px
                                  px-16px
                                  bg-semanticError
                                  text-white
                                  rounded-8px
                                  text-style-button2
                                  hover:bg-semanticError-70%
                                  transition-colors
                                `}
                              >
                                삭제
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {lastPage > 1 && (
                  <div className="flex items-center justify-center gap-8px mt-32px">
                    <button
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(1, prev - 1))
                      }
                      disabled={currentPage === 1}
                      className={`
                      py-8px
                      px-16px
                      rounded-8px
                      text-style-button2
                      border
                      ${
                        currentPage === 1
                          ? 'border-brandPrimary-10% text-textColorText3 cursor-not-allowed'
                          : 'border-brandPrimary-20% text-textColorText2 hover:bg-4B3621-80%-5%'
                      }
                    `}
                    >
                      이전
                    </button>
                    <div className="text-style-body2 text-textColorText2">
                      {currentPage} / {lastPage}
                    </div>
                    <button
                      onClick={() =>
                        setCurrentPage((prev) => Math.min(lastPage, prev + 1))
                      }
                      disabled={currentPage === lastPage}
                      className={`
                      py-8px
                      px-16px
                      rounded-8px
                      text-style-button2
                      border
                      ${
                        currentPage === lastPage
                          ? 'border-brandPrimary-10% text-textColorText3 cursor-not-allowed'
                          : 'border-brandPrimary-20% text-textColorText2 hover:bg-4B3621-80%-5%'
                      }
                    `}
                    >
                      다음
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>

      {/* 생성 모달 */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-16px p-32px max-w-[600px] w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-style-title3 text-textColorText2 mb-24px">
              공지사항 작성
            </h3>
            <form onSubmit={handleSubmitCreate}>
              <div className="mb-24px">
                <AdminInput
                  label="제목"
                  required
                  type="text"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  maxLength={200}
                  labelClassName="block text-style-labelL text-textColorText2 mb-8px"
                  className="py-12px px-16px text-style-body1"
                />
              </div>
              <div className="mb-24px">
                <label className="block text-style-labelL text-textColorText2 mb-8px">
                  내용
                </label>
                <textarea
                  value={formData.content}
                  onChange={(e) =>
                    setFormData({ ...formData, content: e.target.value })
                  }
                  required
                  rows={10}
                  className={`
                    w-full
                    py-12px
                    px-16px
                    border
                    border-brandPrimary-20%
                    rounded-8px
                    text-style-body1
                    text-textColorText2
                    bg-transparent
                    focus:outline-none
                    focus:border-brandPrimary
                    resize-none
                  `}
                />
              </div>
              <div className="flex gap-12px justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setIsCreateModalOpen(false)
                    setFormData({ title: '', content: '' })
                  }}
                  className="py-12px px-24px border border-brandPrimary-20% text-textColorText2 rounded-8px text-style-button2 hover:bg-backgroundColorBg2 transition-colors"
                >
                  취소
                </button>
                <button
                  type="submit"
                  className="py-12px px-24px bg-4B3621-80% text-white rounded-8px text-style-button2 hover:bg-4B3621-80%-70% transition-colors"
                >
                  작성
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 수정 모달 */}
      {isEditModalOpen && selectedNotice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-16px p-32px max-w-[600px] w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-style-title3 text-textColorText2 mb-24px">
              공지사항 수정
            </h3>
            <form onSubmit={handleSubmitEdit}>
              <div className="mb-24px">
                <AdminInput
                  label="제목"
                  required
                  type="text"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  maxLength={200}
                  labelClassName="block text-style-labelL text-textColorText2 mb-8px"
                  className="py-12px px-16px text-style-body1"
                />
              </div>
              <div className="mb-24px">
                <label className="block text-style-labelL text-textColorText2 mb-8px">
                  내용
                </label>
                <textarea
                  value={formData.content}
                  onChange={(e) =>
                    setFormData({ ...formData, content: e.target.value })
                  }
                  required
                  rows={10}
                  className={`
                    w-full
                    py-12px
                    px-16px
                    border
                    border-brandPrimary-20%
                    rounded-8px
                    text-style-body1
                    text-textColorText2
                    bg-transparent
                    focus:outline-none
                    focus:border-brandPrimary
                    resize-none
                  `}
                />
              </div>
              <div className="flex gap-12px justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setIsEditModalOpen(false)
                    setSelectedNotice(null)
                    setFormData({ title: '', content: '' })
                  }}
                  className="py-12px px-24px border border-brandPrimary-20% text-textColorText2 rounded-8px text-style-button2 hover:bg-backgroundColorBg2 transition-colors"
                >
                  취소
                </button>
                <button
                  type="submit"
                  className="py-12px px-24px bg-4B3621-80% text-white rounded-8px text-style-button2 hover:bg-4B3621-80%-70% transition-colors"
                >
                  수정
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminNotices
